import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Briefcase, 
  Sparkles, 
  Heart, 
  Camera, 
  Users, 
  User,
  ChevronRight,
  Clock,
  Shirt,
  MapPin,
  Wand2,
  FileCheck
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { getServiceSchema } from '../components/SEO';

const HeadshotsPortraits = () => {
  const serviceSchema = getServiceSchema({
    name: 'Professional Headshots & Portrait Photography',
    description: 'Professional headshot and portrait photography for business professionals, executives, and personal branding in Arizona.',
    url: 'https://rcstudios.net/services/headshots-portraits',
    image: 'https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/5jr041gd_Portrait-hero.jpg',
    areaServed: 'Arizona',
    priceRange: '$199-$499'
  });

  const useCases = [
    {
      icon: Briefcase,
      title: "Business Headshots",
      desc: "Professional portraits for LinkedIn, company websites, and corporate communications."
    },
    {
      icon: Sparkles,
      title: "Branding Portraits",
      desc: "Curated imagery that captures your personal brand and visual identity."
    },
    {
      icon: Camera,
      title: "Lifestyle Portraits",
      desc: "Natural, relaxed portraits in environments that tell your story."
    },
    {
      icon: User,
      title: "Actor & Creator",
      desc: "Versatile headshots for auditions, press kits, and social media presence."
    },
    {
      icon: Users,
      title: "Couples",
      desc: "Intimate portraits celebrating connection and partnership."
    },
    {
      icon: Heart,
      title: "Personal Sessions",
      desc: "Private portrait experiences tailored to your vision and comfort."
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Plan",
      desc: "We start with a consultation to understand your goals, discuss wardrobe options, and select the right setting for your session."
    },
    {
      step: "02",
      title: "Shoot",
      desc: "During your session, I provide direction and guidance to help you feel confident and relaxed in front of the camera."
    },
    {
      step: "03",
      title: "Edit & Deliver",
      desc: "All editing is done in-house, by me. Your final images are carefully retouched and delivered with a polished, natural finish."
    }
  ];

  const faqs = [
    {
      question: "What is the typical turnaround time?",
      answer: "Most portrait sessions are delivered within 5–7 business days. Rush delivery is available upon request for an additional fee."
    },
    {
      question: "Do you provide wardrobe guidance?",
      answer: "Yes. After booking, I'll send you a preparation guide with recommendations on colors, styles, and what to avoid based on your session type and goals."
    },
    {
      question: "Can sessions be done on location?",
      answer: "Absolutely. I offer both studio and on-location sessions. Location shoots can take place at your office, home, or a meaningful outdoor setting—wherever best fits the vision for your portraits."
    },
    {
      question: "What is your retouching approach?",
      answer: "My editing style is clean and natural. I focus on enhancing lighting, color, and skin tone while keeping the result authentic to you. Heavy filtering or artificial smoothing isn't part of my process."
    },
    {
      question: "What about licensing and usage rights?",
      answer: "All portrait packages include personal and professional usage rights. Commercial licensing for advertising or third-party use is available and discussed during booking."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Professional Headshots & Portrait Photography"
        description="Professional headshot and portrait photography in Kingman, Arizona. Business headshots, executive portraits, and personal branding sessions starting at $199."
        path="/services/headshots-portraits"
        image="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/5jr041gd_Portrait-hero.jpg"
        jsonLd={serviceSchema}
      />
      {/* Hero Section - Clean Image Only */}
      <section className="hero-main relative h-[400px] md:h-[480px] lg:h-[520px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/5jr041gd_Portrait-hero.jpg"
            alt="Professional portrait photography"
            className="w-full h-full object-cover object-[center_20%]"
          />
          {/* Subtle cinematic vignette for navbar contrast only */}
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#050816]/50 to-transparent"></div>
        </div>
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 md:py-6">
          <div className="flex items-center gap-3">
            <Link 
              to="/services" 
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#F5C056] hover:text-[#F5C056]/80 transition-colors"
            >
              Services
            </Link>
            <span className="text-[#050816]/30 text-xs">/</span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#050816]">
              Headshots & Portraits
            </span>
          </div>
          <div className="w-12 h-[1.5px] bg-[#F5C056]/40 mt-4"></div>
        </div>
      </section>

      {/* What This Is Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-[900px]">
          <div className="text-center space-y-6">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              More Than Just a Photo
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                A great portrait isn&apos;t just technically correct—it captures something real. Whether you need a polished headshot for your business or a more personal portrait that reflects who you are, I approach every session with intention and care.
              </p>
              <p>
                I provide hands-on direction throughout the shoot, helping you feel comfortable and confident in front of the camera. Every image is edited in-house, ensuring a consistent, high-quality result that feels authentic to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Portrait Gallery */}
      <section className="pt-24 pb-16 bg-[#050816] overflow-hidden">
        <div className="container mx-auto px-6 max-w-[1400px]">
          {/* Section Header */}
          <div className="text-center mb-20 space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl text-white">Visual Style</h2>
            <p className="text-base text-gray-400 max-w-md mx-auto">
              Examples shown to illustrate creative style and possibilities.
            </p>
          </div>

          {/* Desktop: Layered Editorial Gallery */}
          <div className="hidden lg:block relative h-[560px] max-w-[1200px] mx-auto">
            {/* Image 1 - Large left */}
            <div className="absolute left-0 top-8 w-[320px] group">
              <div className="relative overflow-hidden shadow-2xl transition-all duration-700 ease-out group-hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] group-hover:-translate-y-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/lec1hio9_ChatGPT%20Image%20Feb%205%2C%202026%2C%2006_55_14%20AM.png"
                  alt="Portrait example"
                  className="w-full h-[420px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>

            {/* Image 2 - Medium center-left, overlapping */}
            <div className="absolute left-[280px] top-[180px] w-[280px] group z-10">
              <div className="relative overflow-hidden shadow-2xl transition-all duration-700 ease-out group-hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] group-hover:-translate-y-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/mlhor76o_z-image_00092_.png"
                  alt="Portrait example"
                  className="w-full h-[380px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>

            {/* Image 3 - Tall center */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[300px] group z-20">
              <div className="relative overflow-hidden shadow-2xl transition-all duration-700 ease-out group-hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] group-hover:-translate-y-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/qv7k7gzd_z-image_00079_.png"
                  alt="Portrait example"
                  className="w-full h-[480px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>

            {/* Image 4 - Medium center-right */}
            <div className="absolute right-[280px] top-[200px] w-[260px] group z-10">
              <div className="relative overflow-hidden shadow-2xl transition-all duration-700 ease-out group-hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] group-hover:-translate-y-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/g1yxdu8x_z-image_00083_.png"
                  alt="Portrait example"
                  className="w-full h-[360px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>

            {/* Image 5 - Large right */}
            <div className="absolute right-0 top-[40px] w-[300px] group">
              <div className="relative overflow-hidden shadow-2xl transition-all duration-700 ease-out group-hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] group-hover:-translate-y-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/9ans0qsu_z-image_00086_.png"
                  alt="Portrait example"
                  className="w-full h-[400px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>
          </div>

          {/* Tablet: 2-column staggered */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="group overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/lec1hio9_ChatGPT%20Image%20Feb%205%2C%202026%2C%2006_55_14%20AM.png"
                  alt="Portrait example"
                  className="w-full h-[400px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
              <div className="group overflow-hidden shadow-xl mt-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/qv7k7gzd_z-image_00079_.png"
                  alt="Portrait example"
                  className="w-full h-[350px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>
            <div className="space-y-6 pt-12">
              <div className="group overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/mlhor76o_z-image_00092_.png"
                  alt="Portrait example"
                  className="w-full h-[350px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
              <div className="group overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/g1yxdu8x_z-image_00083_.png"
                  alt="Portrait example"
                  className="w-full h-[400px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
              <div className="group overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/9ans0qsu_z-image_00086_.png"
                  alt="Portrait example"
                  className="w-full h-[320px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.02]"
                />
              </div>
            </div>
          </div>

          {/* Mobile: Clean stacked */}
          <div className="md:hidden space-y-6">
            {[
              { src: "https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/lec1hio9_ChatGPT%20Image%20Feb%205%2C%202026%2C%2006_55_14%20AM.png", h: "h-[400px]" },
              { src: "https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/qv7k7gzd_z-image_00079_.png", h: "h-[450px]" },
              { src: "https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/mlhor76o_z-image_00092_.png", h: "h-[380px]" },
              { src: "https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/g1yxdu8x_z-image_00083_.png", h: "h-[400px]" },
              { src: "https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/9ans0qsu_z-image_00086_.png", h: "h-[360px]" }
            ].map((img, i) => (
              <div key={i} className="group overflow-hidden shadow-xl">
                <img 
                  src={img.src}
                  alt="Portrait example"
                  className={`w-full ${img.h} object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">Who This Is For</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Portrait sessions tailored to a range of personal and professional needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((item, i) => (
              <Card key={i} className="border-none shadow-sm hover:shadow-md transition-all duration-300 bg-white group">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-full bg-[#F5C056]/10 flex items-center justify-center mb-6 text-[#F5C056] group-hover:bg-[#F5C056]/20 transition-colors">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#050816] text-white">
        <div className="container mx-auto px-6 max-w-[1100px]">
          <div className="text-center mb-16 space-y-4">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase">The Experience</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">From Concept to Final Portrait</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              A guided, personalized experience designed to create images you&apos;re proud to use and share.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Step 01 */}
            <div className="group relative bg-gradient-to-b from-[#0d0d18] to-[#0a0a14] rounded-2xl p-8 border border-white/5 hover:border-[#F5C056]/20 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(245,192,86,0.15)]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#F5C056]/40 to-transparent rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="mb-6">
                <span className="text-5xl font-serif font-bold text-[#F5C056]/20 group-hover:text-[#F5C056]/40 transition-colors duration-500">01</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Creative Direction</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                We begin with a one-on-one consultation to understand your goals, style, and how your images will be used. Every session is planned with intention.
              </p>
            </div>

            {/* Step 02 */}
            <div className="group relative bg-gradient-to-b from-[#0d0d18] to-[#0a0a14] rounded-2xl p-8 border border-white/5 hover:border-[#F5C056]/20 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(245,192,86,0.15)]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#F5C056]/40 to-transparent rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="mb-6">
                <span className="text-5xl font-serif font-bold text-[#F5C056]/20 group-hover:text-[#F5C056]/40 transition-colors duration-500">02</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Guided Session</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                During the shoot, I provide clear direction throughout so you feel confident, relaxed, and camera-ready at every step.
              </p>
            </div>

            {/* Step 03 */}
            <div className="group relative bg-gradient-to-b from-[#0d0d18] to-[#0a0a14] rounded-2xl p-8 border border-white/5 hover:border-[#F5C056]/20 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(245,192,86,0.15)]">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#F5C056]/40 to-transparent rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="mb-6">
                <span className="text-5xl font-serif font-bold text-[#F5C056]/20 group-hover:text-[#F5C056]/40 transition-colors duration-500">03</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Hand-Finished Delivery</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Every image is edited in-house by me—no outsourcing, no presets—resulting in clean, natural portraits with a refined finish.
              </p>
            </div>
          </div>

          {/* In-house editing callout - refined */}
          <div className="mt-12 p-6 bg-gradient-to-r from-[#F5C056]/5 via-[#F5C056]/10 to-[#F5C056]/5 rounded-xl border border-[#F5C056]/10">
            <div className="flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
              <div className="w-12 h-12 rounded-full bg-[#F5C056]/20 flex items-center justify-center text-[#F5C056] shrink-0">
                <Wand2 className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-base font-bold text-white mb-1">100% In-House Editing</h4>
                <p className="text-gray-400 text-sm">
                  No templates. No outsourcing. Each image receives personal attention for a polished, natural result.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Block */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-b from-[#12121c] to-[#0d0d16] rounded-2xl p-10 md:p-12 border border-white/5">
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">Ready to Get Started?</h3>
              <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                View availability and select the portrait experience that fits your needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild 
                  className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                  <Link to="/book?type=other">
                    Order Headshots & Portraits
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 hover:border-white/30 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all bg-transparent"
                >
                  <Link to="/contact">
                    Request a Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[800px]">
          <div className="text-center mb-12 space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">Quick FAQ</h2>
            <p className="text-lg text-muted-foreground">
              Common questions about portrait sessions.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem 
                key={i} 
                value={`item-${i}`}
                className="bg-white rounded-xl border border-gray-100 px-6 shadow-sm"
              >
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-5">
                  <div className="flex items-center gap-4">
                    {i === 0 && <Clock className="w-5 h-5 text-[#F5C056] shrink-0" />}
                    {i === 1 && <Shirt className="w-5 h-5 text-[#F5C056] shrink-0" />}
                    {i === 2 && <MapPin className="w-5 h-5 text-[#F5C056] shrink-0" />}
                    {i === 3 && <Wand2 className="w-5 h-5 text-[#F5C056] shrink-0" />}
                    {i === 4 && <FileCheck className="w-5 h-5 text-[#F5C056] shrink-0" />}
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 pl-9">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#050816] text-white">
        <div className="container mx-auto px-6 text-center max-w-[700px]">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
            Ready to Book Your Session?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Let&apos;s discuss your vision and find the right approach for your portraits.
          </p>
          <Button 
            asChild 
            className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-10 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
          >
            <Link to="/contact">
              Request Availability
              <ChevronRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HeadshotsPortraits;
